import PropTypes from 'prop-types';

import {ClientId, FromServerId, NLOId, ToServerId} from 'notebook/ELNConstants';
import {
  BaseStringStructuredTableColumn,
  BaseTagValueStructuredTableColumn,
  BaseTagValueStructuredTableColumnWithMissingField,
  DATA_TABLE_COLUMN_SHAPE,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell, cell} from 'notebook/tables/constants/CellConstants';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {ASSAY_SCHEMA_SHAPE, AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import {Optional} from 'util/Types';

type GenericDataTableColumn<TIds> =
  // Column with schema field
  | BaseTagValueStructuredTableColumn<TIds>
  // Columns with deleted schema field
  | BaseTagValueStructuredTableColumnWithMissingField<TIds>
  // Column corresponding to a custom tag
  | BaseStringStructuredTableColumn<TIds>;

export type ToServerDataTableColumn = GenericDataTableColumn<ToServerId>;
export type FromServerDataTableColumn = GenericDataTableColumn<FromServerId>;
export type DataTableColumn = GenericDataTableColumn<ClientId>;

export interface DataTableDataKey {
  batchId?: Optional<string>;
  fileId?: Optional<string>;
  stageRunId?: Optional<string>;
  stageRunSampleId?: Optional<string>;
}

export type Row = DataTableDataKey & {
  euid: string;
  cells: Array<Cell>;
};

export interface ImportedRow {
  dataKey: DataTableDataKey;
  cellValues: Array<{columnName: string; text: string}>;
}

export interface ImportedRowUpdate {
  euid: string;
  cellUpdates: Array<{columnName: string; text: string}>;
}

export type StructuredData = Array<{
  stageRunId: string;
  stageRunSampleId: string;
  fields: Array<{
    tableName: string;
    name: string;
    value: string;
    important: boolean;
  }>;
}>;

export enum DATA_TABLE_TYPE {
  STAGE_RUN_SAMPLE_LINK_TABLE = 'STAGE_RUN_SAMPLE_TABLE',
  BATCH_LINK_TABLE = 'BATCH_LINK_TABLE',
  FILE_LINK_TABLE = 'FILE_LINK_TABLE',
  NO_LINK_TABLE = 'NO_LINK_TABLE',
}

export const DEFAULT_DEFAULT_VALUE = {text: ''};

export type GenericDataTableConfig<TIds> = {
  assaySchema: AssaySchema;
  dataType?: string;
  provenanceUuid: string | null;
  isStandalone: boolean;
  isWatchingInputTable: boolean;
  linkType: string | null;
  name: string;
  type: NoteLinkedObjectType.DATA_TABLE_CONFIG;
  watchedRegistrationProvenanceUuids: Array<string>;
  columns: Array<GenericDataTableColumn<TIds>>;
} & NLOId<TIds>;

export type DataTableConfig = GenericDataTableConfig<ClientId>;
export type ToServerDataTableConfig = GenericDataTableConfig<ToServerId>;
export type FromServerDataTableConfig = GenericDataTableConfig<FromServerId>;

export const DATA_TABLE_CONFIG_SHAPE = PropTypes.shape({
  linkType: PropTypes.oneOf([FIELD_TYPES.BATCH_LINK.type, FIELD_TYPES.BIOENTITY_LINK.type]),
  assaySchema: ASSAY_SCHEMA_SHAPE.isRequired,
  columns: PropTypes.arrayOf(DATA_TABLE_COLUMN_SHAPE.isRequired).isRequired,
  provenanceUuid: PropTypes.string,
  isStandalone: PropTypes.bool.isRequired,
  isWatchingInputTable: PropTypes.bool.isRequired,
  watchedRegistrationProvenanceUuids: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
});

export type GenericDataTable<TIds> = {
  provenanceUuid: string | null;
  rows: Array<Row>;
  type: NoteLinkedObjectType.DATA_TABLE;
  config: GenericDataTableConfig<TIds>;
} & NLOId<TIds>;

export type DataTable = GenericDataTable<ClientId>;
export type ToServerDataTable = GenericDataTable<ToServerId>;
export type FromServerDataTable = GenericDataTable<FromServerId>;

export const dataTable = PropTypes.shape({
  provenanceUuid: PropTypes.string,
  config: DATA_TABLE_CONFIG_SHAPE.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      euid: PropTypes.string.isRequired,
      batchId: PropTypes.string,
      fileId: PropTypes.string,
      stageRunId: PropTypes.string,
      stageRunSampleId: PropTypes.string,
      cells: PropTypes.arrayOf(cell.isRequired).isRequired,
    }).isRequired
  ).isRequired,
});

export const MIN_NEW_ROW_COUNT = 1;
export const MAX_NEW_ROW_COUNT = 500;

export interface DataTableErrorsByRecordId {
  [warehouseId: string]: {[fieldDbId: number]: string};
}
